/**
 * Employee Desktop Dashboard — Tableau de bord espace salarié
 */
import React from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { FileText, FolderOpen, Receipt, Calendar, Palmtree, Clock, ChevronRight } from 'lucide-react';

const QUICK_LINKS = [
  { to: '/employees/contract', label: 'Mon contrat', icon: FileText, color: 'text-[#C9A24D] bg-[#C9A24D]/10' },
  { to: '/employees/documents', label: 'Documents RH', icon: FolderOpen, color: 'text-blue-400 bg-blue-400/10' },
  { to: '/employees/payslips', label: 'Bulletins de salaire', icon: Receipt, color: 'text-emerald-400 bg-emerald-400/10' },
  { to: '/employees/schedule', label: 'Mon planning', icon: Calendar, color: 'text-purple-400 bg-purple-400/10' },
  { to: '/employees/leave', label: 'Mes congés', icon: Palmtree, color: 'text-amber-400 bg-amber-400/10' },
  { to: '/employees/history', label: 'Historique heures', icon: Clock, color: 'text-cyan-400 bg-cyan-400/10' },
];

export default function EmployeeDashboardPage() {
  const { dashboard } = useOutletContext();

  return (
    <div className="p-8 max-w-5xl" data-testid="employee-dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white">
          Bonjour, <span className="text-[#C9A24D]">{dashboard?.driver_name?.split(' ')[0] || 'Salarié'}</span>
        </h1>
        <p className="text-white/40 text-sm mt-1">Bienvenue dans votre espace salarié</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5" data-testid="stat-documents">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Documents</span>
            <FolderOpen className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white">{dashboard?.documents_count || 0}</p>
        </div>

        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5" data-testid="stat-payslips">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Bulletins</span>
            <Receipt className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-white">{dashboard?.payslips_count || 0}</p>
        </div>

        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5" data-testid="stat-leave-remaining">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Congés restants</span>
            <Palmtree className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-2xl font-bold text-white">{dashboard?.vacation_days_remaining || 0}<span className="text-base text-white/30 ml-1">/ {dashboard?.vacation_days_total || 25}</span></p>
        </div>

        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5" data-testid="stat-leave-pending">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">Congés en attente</span>
            <Calendar className="w-4 h-4 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-white">{dashboard?.leave_pending || 0}</p>
        </div>
      </div>

      {/* Quick links grid */}
      <div>
        <h2 className="text-white/60 text-xs font-medium uppercase tracking-wider mb-4">Accès rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {QUICK_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition group"
              data-testid={`quick-link-${link.to.split('/').pop()}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${link.color}`}>
                <link.icon className="w-5 h-5" />
              </div>
              <span className="text-white/70 text-sm font-medium group-hover:text-white transition flex-1">{link.label}</span>
              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
